import { useEffect, useState } from 'react';
import {
  CATEGORY_LABEL,
  DEFAULT_AWAKE_ACTIVITIES,
  DEFAULT_CRY_REASONS,
  DEFAULT_CRY_SOOTHE,
  DEFAULT_GYM_KINDS,
  DEFAULT_SLEEP_CLOTHES,
  DEFAULT_SLEEP_METHODS,
  DEFAULT_SLEEP_PLACES,
  DEFAULT_SLEEP_POSITIONS,
  can,
  formatAge,
  localDate,
  INVITABLE_ROLES,
  ROLE_LABEL,
  type CustomKind,
  type EventCategory,
  type MemberDto,
  type ReminderSettingsDto,
  type Role,
} from '@bb/shared/core';
import { api } from '../api';
import { Chips, Icon, Seg } from '../components';
import { initials, type ThemeMode } from '../lib';
import { platform } from '../platform';
import { useApp } from '../state';

const INTERVALS: { value: string; label: string }[] = [
  { value: 'off', label: 'Выкл' },
  { value: '120', label: '2 ч' },
  { value: '150', label: '2,5 ч' },
  { value: '180', label: '3 ч' },
  { value: '210', label: '3,5 ч' },
  { value: '240', label: '4 ч' },
];

function ChildrenSection() {
  const ctx = useApp();
  const { child, family } = ctx;
  const editable = can(family.role, 'child.edit');
  const isOwner = can(family.role, 'family.manage');
  const today = localDate(Date.now(), family.tz);
  const [name, setName] = useState(child.name);
  const [birthDate, setBirthDate] = useState(child.birthDate);
  const [sex, setSex] = useState<'male' | 'female' | null>(child.sex);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    setName(child.name);
    setBirthDate(child.birthDate);
    setSex(child.sex);
  }, [child]);
  const dirty = name.trim() !== child.name || birthDate !== child.birthDate || sex !== child.sex;

  async function save() {
    setBusy(true);
    try {
      await api.updateChild(child.id, { name: name.trim(), birthDate, sex });
      await ctx.reloadMe();
      ctx.toast({ text: 'Сохранено' });
    } catch (e) {
      ctx.fail(e);
    }
    setBusy(false);
  }

  async function remove() {
    if (!(await platform.confirm(`Удалить ${child.name} и все записи? Это нельзя отменить.`))) return;
    try {
      await api.deleteChild(child.id);
      const other = family.children.find((c) => c.id !== child.id);
      if (other) ctx.selectChild(other.id);
      await ctx.reloadMe();
      ctx.toast({ text: 'Удалено' });
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Малыши">
      <span className="section-title">Малыши</span>
      {family.children.map((c) => (
        <button key={c.id} type="button" className={`pick${c.id === child.id ? ' on' : ''}`} aria-pressed={c.id === child.id} onClick={() => ctx.selectChild(c.id)} data-testid="child-pick">
          <div className="avatar" style={{ width: 40, height: 40, fontSize: 15 }} aria-hidden="true">
            {initials(c.name)}
          </div>
          <span className="grow" style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontWeight: 600 }}>{c.name}</span>
            <span className="tiny muted">{formatAge(c.birthDate, today)}</span>
          </span>
          {c.id === child.id ? <span className="tiny" style={{ fontWeight: 600 }}>выбран</span> : <Icon name="right" size={16} />}
        </button>
      ))}

      <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <span className="label small" style={{ fontWeight: 600 }}>
          Данные: {child.name}
        </span>
        <div className="field">
          <label htmlFor="s-name">Имя</label>
          <input id="s-name" className="input" value={name} disabled={!editable} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="s-birth">Дата рождения</label>
          <input id="s-birth" className="input" type="date" value={birthDate} max={today} disabled={!editable} onChange={(e) => setBirthDate(e.target.value)} />
        </div>
        <div className="field">
          <span className="label">Пол — для сравнения роста и веса с нормами ВОЗ</span>
          <Chips<'male' | 'female'> label="Пол" value={sex} options={[{ value: 'female', label: 'Девочка' }, { value: 'male', label: 'Мальчик' }]} onChange={(v) => editable && setSex(v)} />
        </div>
        {editable && dirty && (
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim() || !birthDate} onClick={() => void save()}>
            Сохранить
          </button>
        )}
        {isOwner && family.children.length > 1 && (
          <button type="button" className="btn btn-danger" onClick={() => void remove()}>
            <Icon name="trash" size={18} /> Удалить «{child.name}»
          </button>
        )}
      </div>

      {editable && (adding ? <AddChild onDone={() => setAdding(false)} /> : (
        <button type="button" className="btn btn-soft" onClick={() => setAdding(true)}>
          <Icon name="plus" size={18} /> Добавить малыша
        </button>
      ))}
    </section>
  );
}

function AddChild({ onDone }: { onDone: () => void }) {
  const ctx = useApp();
  const today = localDate(Date.now(), ctx.family.tz);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [sex, setSex] = useState<'male' | 'female' | null>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    try {
      const c = await api.addChild(ctx.family.id, { name: name.trim(), birthDate, sex, birthWeightG: null, feeding: null });
      await ctx.reloadMe();
      ctx.selectChild(c.id);
      ctx.toast({ text: `${c.name} добавлен(а)` });
      onDone();
    } catch (e) {
      ctx.fail(e);
    }
    setBusy(false);
  }

  return (
    <div className="stack detail" data-testid="add-child">
      <span className="label small" style={{ fontWeight: 600 }}>
        Новый малыш
      </span>
      <div className="field">
        <label htmlFor="n-name">Имя</label>
        <input id="n-name" className="input" value={name} maxLength={60} placeholder="Например, Миша" onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="n-birth">Дата рождения</label>
        <input id="n-birth" className="input" type="date" value={birthDate} max={today} onChange={(e) => setBirthDate(e.target.value)} />
      </div>
      <Chips<'male' | 'female'> label="Пол" value={sex} options={[{ value: 'female', label: 'Девочка' }, { value: 'male', label: 'Мальчик' }]} onChange={setSex} />
      <div className="tiles2">
        <button type="button" className="btn btn-secondary" onClick={onDone}>
          Отмена
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || !name.trim() || !birthDate} onClick={() => void add()}>
          Добавить
        </button>
      </div>
    </div>
  );
}

type DirKey = 'sleepPlaces' | 'sleepClothes' | 'sleepPositions' | 'sleepMethods' | 'cryReasons' | 'crySoothe' | 'gymKinds' | 'awakeActivities';
const DIRS: { key: DirKey; title: string; defaults: string[] }[] = [
  { key: 'sleepPlaces', title: 'Где спит', defaults: DEFAULT_SLEEP_PLACES },
  { key: 'sleepClothes', title: 'Одежда для сна', defaults: DEFAULT_SLEEP_CLOTHES },
  { key: 'sleepPositions', title: 'Как лежит', defaults: DEFAULT_SLEEP_POSITIONS },
  { key: 'sleepMethods', title: 'Как укладываем', defaults: DEFAULT_SLEEP_METHODS },
  { key: 'awakeActivities', title: 'Занятия в бодрствование', defaults: DEFAULT_AWAKE_ACTIVITIES },
  { key: 'cryReasons', title: 'Причины плача', defaults: DEFAULT_CRY_REASONS },
  { key: 'crySoothe', title: 'Что помогло успокоить', defaults: DEFAULT_CRY_SOOTHE },
  { key: 'gymKinds', title: 'Гимнастика и массаж', defaults: DEFAULT_GYM_KINDS },
];

/** Справочники семьи: варианты, которые предлагаются при записи сна. */
function DirectoriesSection() {
  const ctx = useApp();
  const editable = can(ctx.family.role, 'child.edit');
  const s = ctx.family.settings ?? {};
  const [draft, setDraft] = useState<Record<string, string>>({});

  async function save(key: DirKey, list: string[]) {
    try {
      await api.saveFamilySettings(ctx.family.id, { [key]: list });
      await ctx.reloadMe();
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Справочники">
      <span className="section-title">Справочники</span>
      <span className="tiny muted">Варианты, которые предлагаются при записи сна, плача, гимнастики и занятий.</span>
      {DIRS.map((d) => {
        const list = s[d.key]?.length ? s[d.key]! : d.defaults;
        return (
          <div key={d.key} className="field">
            <span className="label">{d.title}</span>
            <div className="chips">
              {list.map((x) => (
                <span key={x} className="chip dir-chip">
                  {x}
                  {editable && (
                    <button type="button" aria-label={`Убрать «${x}»`} onClick={() => void save(d.key, list.filter((y) => y !== x))}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            {editable && (
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  const v = (draft[d.key] ?? '').trim();
                  if (!v || list.includes(v)) return;
                  setDraft({ ...draft, [d.key]: '' });
                  void save(d.key, [...list, v]);
                }}
              >
                <input className="input grow" value={draft[d.key] ?? ''} maxLength={40} placeholder="Добавить вариант" onChange={(e) => setDraft({ ...draft, [d.key]: e.target.value })} aria-label={`${d.title}: новый вариант`} />
                <button type="submit" className="btn btn-soft">
                  <Icon name="plus" size={18} />
                </button>
              </form>
            )}
          </div>
        );
      })}
      {editable && (
        <button type="button" className="link-btn" style={{ alignSelf: 'flex-start' }} onClick={() => void Promise.all(DIRS.map((d) => api.saveFamilySettings(ctx.family.id, { [d.key]: d.defaults }))).then(() => ctx.reloadMe(), ctx.fail)}>
          Вернуть списки по умолчанию
        </button>
      )}
    </section>
  );
}

function FamilySection() {
  const ctx = useApp();
  const { family } = ctx;
  const isOwner = can(family.role, 'family.manage');
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [inviteRole, setInviteRole] = useState<Role>('parent');
  const kids = family.children;
  // null — на всех малышей (и тех, кто появится позже); иначе — только выбранные.
  const [inviteKids, setInviteKids] = useState<string[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [v, setV] = useState(0);

  useEffect(() => {
    api.members(family.id).then(setMembers, ctx.fail);
  }, [family.id, v, ctx.fail]);

  async function invite() {
    try {
      const ids = inviteKids && inviteKids.length && inviteKids.length < kids.length ? inviteKids : null;
      const res = await api.invite(family.id, inviteRole, ids);
      const url = res.link ?? res.token;
      setLink(url);
      const names = ids ? kids.filter((k) => ids.includes(k.id)).map((k) => k.name).join(', ') : kids.length === 1 ? kids[0]!.name : 'наших малышей';
      const shared = await platform.shareLink(url, `Присоединяйтесь к дневнику: ${names}`);
      if (platform.name === 'browser') ctx.toast({ text: shared ? 'Ссылка скопирована' : 'Скопируйте ссылку ниже' });
    } catch (e) {
      ctx.fail(e);
    }
  }

  async function changeRole(m: MemberDto, role: Role) {
    try {
      await api.updateMember(family.id, m.userId, { role });
      setV((n) => n + 1);
    } catch (e) {
      ctx.fail(e);
    }
  }

  async function changeKids(m: MemberDto, childIds: string[] | null) {
    try {
      await api.updateMember(family.id, m.userId, { childIds: childIds && childIds.length < kids.length ? childIds : null });
      setV((n) => n + 1);
    } catch (e) {
      ctx.fail(e);
    }
  }

  async function remove(m: MemberDto) {
    if (!(await platform.confirm(`Убрать ${m.name} из семьи?`))) return;
    try {
      await api.removeMember(family.id, m.userId);
      setV((n) => n + 1);
    } catch (e) {
      ctx.fail(e);
    }
  }

  return (
    <section className="card stack" aria-label="Семья">
      <span className="section-title">Семья</span>
      {!members ? (
        <div className="skeleton" />
      ) : (
        members.map((m) => (
          <div key={m.userId} className="stack" style={{ gap: 8 }} data-testid="member">
            <div className="row">
              <div className="avatar" style={{ width: 36, height: 36, fontSize: 14 }} aria-hidden="true">
                {initials(m.name)}
              </div>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>
                  {m.name}
                  {m.isMe ? ' (вы)' : ''}
                </div>
                <div className="tiny muted">
                  {ROLE_LABEL[m.role]}
                  {kids.length > 1 && m.role !== 'owner' && ` · видит: ${m.childIds ? kids.filter((k) => m.childIds!.includes(k.id)).map((k) => k.name).join(', ') || '—' : 'всех малышей'}`}
                </div>
              </div>
              {isOwner && m.role !== 'owner' && (
                <button type="button" className="icon-btn" aria-label={`Убрать ${m.name}`} onClick={() => void remove(m)}>
                  <Icon name="trash" size={18} />
                </button>
              )}
            </div>
            {isOwner && m.role !== 'owner' && (
              <Chips<Role> label={`Роль ${m.name}`} value={m.role} options={INVITABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))} onChange={(r) => void changeRole(m, r)} />
            )}
            {isOwner && m.role !== 'owner' && kids.length > 1 && <KidsPicker label={`Какие малыши доступны ${m.name}`} kids={kids} value={m.childIds} onChange={(ids) => void changeKids(m, ids)} />}
          </div>
        ))
      )}
      {isOwner && (
        <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <span className="label small" style={{ fontWeight: 600 }}>
            Пригласить
          </span>
          <Seg<Role> label="Роль приглашённого" value={inviteRole} options={INVITABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))} onChange={setInviteRole} />
          <p className="tiny muted">{ROLE_HINT[inviteRole]}</p>
          {kids.length > 1 && <KidsPicker label="На каких малышей приглашение" kids={kids} value={inviteKids} onChange={setInviteKids} />}
          <button type="button" className="btn btn-soft" onClick={() => void invite()}>
            <Icon name="share" size={18} /> Отправить приглашение
          </button>
          {link && (
            <input className="input small" readOnly value={link} aria-label="Ссылка-приглашение" onFocus={(e) => e.target.select()} data-testid="invite-link" />
          )}
          <p className="tiny muted">Ссылка одноразовая и действует 7 дней.</p>
        </div>
      )}
    </section>
  );
}

const ROLE_HINT: Record<Role, string> = {
  owner: 'Владелец: всё, включая приглашения и удаление малышей.',
  parent: 'Родитель: видит и ведёт дневник, правит и удаляет любые записи, меняет данные малыша и справочники. Не может приглашать, менять роли и удалять малышей.',
  nanny: 'Няня: видит дневник, добавляет и правит записи, запускает таймеры. Чужие записи не удаляет, данные малыша и справочники не меняет.',
  relative: 'Родственник: только смотрит дневник и статистику, ничего не меняет.',
};

/** Выбор малышей: «Все» (и будущие) или конкретные. Пустой выбор не даём — остаётся хотя бы один. */
function KidsPicker({ label, kids, value, onChange }: { label: string; kids: { id: string; name: string }[]; value: string[] | null; onChange: (ids: string[] | null) => void }) {
  const all = value == null;
  const toggle = (id: string) => {
    const cur = value ?? [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    if (!next.length) return;
    onChange(next.length === kids.length ? null : next);
  };
  return (
    <div className="field">
      <span className="label small">{label}</span>
      <div className="chips" role="group" aria-label={label}>
        <button type="button" className="chip" aria-pressed={all} onClick={() => onChange(null)}>
          Все малыши
        </button>
        {kids.map((k) => (
          <button key={k.id} type="button" className="chip" aria-pressed={!all && value.includes(k.id)} onClick={() => (all ? onChange([k.id]) : toggle(k.id))}>
            {k.name}
          </button>
        ))}
      </div>
      <span className="tiny muted">{all ? 'Доступ ко всем, в том числе к малышам, которых добавят позже.' : 'Остальных малышей этот человек не увидит.'}</span>
    </div>
  );
}

const EMOJIS = ['⭐', '💊', '🧴', '👶', '🍼', '🚿', '🦷', '🩺', '🧸', '📚', '🎵', '🚗', '💧', '🤧', '🌡️', '✂️', '👂', '👀'];
const CATS: EventCategory[] = ['awake', 'feed', 'sleep', 'diaper', 'walk', 'cry', 'other'];

/** Свои события семьи: название, значок, категория, длительность или момент, варианты, единица. */
function CustomKindsSection() {
  const ctx = useApp();
  const editable = can(ctx.family.role, 'child.edit');
  const kinds = ctx.family.settings?.customKinds ?? [];
  const [edit, setEdit] = useState<CustomKind | null>(null);
  async function save(list: CustomKind[]) {
    try {
      await api.saveFamilySettings(ctx.family.id, { customKinds: list });
      await ctx.reloadMe();
    } catch (e) {
      ctx.fail(e);
    }
  }
  const blank = (): CustomKind => ({ id: Math.random().toString(36).slice(2, 10), name: '', emoji: '⭐', cat: 'awake', timed: true });
  return (
    <section className="card stack" aria-label="Свои события">
      <span className="section-title">Свои события</span>
      <span className="tiny muted">Отмечайте то, что важно именно вам: массаж, капли в нос, сцеживание, выкладывание на живот… Кнопка появится на главной в «Добавить».</span>
      {kinds.map((k) => (
        <div key={k.id} className="row" data-testid="custom-kind">
          <span style={{ fontSize: 22 }} aria-hidden="true">
            {k.emoji}
          </span>
          <span className="grow">
            <span style={{ display: 'block', fontWeight: 600 }}>{k.name}</span>
            <span className="tiny muted">
              {CATEGORY_LABEL[k.cat]} · {k.timed ? 'с длительностью' : 'момент'}
              {k.options?.length ? ` · ${k.options.join(', ')}` : ''}
              {k.unit ? ` · ${k.unit}` : ''}
            </span>
          </span>
          {editable && (
            <button type="button" className="icon-btn sm" aria-label={`Изменить «${k.name}»`} onClick={() => setEdit(k)}>
              <Icon name="edit" size={16} />
            </button>
          )}
        </div>
      ))}
      {editable &&
        (edit ? (
          <KindForm
            initial={edit}
            isNew={!kinds.some((k) => k.id === edit.id)}
            onCancel={() => setEdit(null)}
            onDelete={() => {
              void save(kinds.filter((k) => k.id !== edit.id));
              setEdit(null);
            }}
            onSave={(k) => {
              void save(kinds.some((x) => x.id === k.id) ? kinds.map((x) => (x.id === k.id ? k : x)) : [...kinds, k]);
              setEdit(null);
            }}
          />
        ) : (
          <button type="button" className="btn btn-soft" onClick={() => setEdit(blank())} data-testid="add-kind">
            <Icon name="plus" size={18} /> Новое событие
          </button>
        ))}
    </section>
  );
}

function KindForm({ initial, isNew, onSave, onCancel, onDelete }: { initial: CustomKind; isNew: boolean; onSave: (k: CustomKind) => void; onCancel: () => void; onDelete: () => void }) {
  const [k, setK] = useState<CustomKind>(initial);
  const [opt, setOpt] = useState('');
  const set = (patch: Partial<CustomKind>) => setK((prev) => ({ ...prev, ...patch }));
  const options = k.options ?? [];
  const valid = k.name.trim().length > 0;
  return (
    <div className="stack detail" data-testid="kind-form">
      <div className="field">
        <label className="label" htmlFor="kind-name">
          Название
        </label>
        <input id="kind-name" className="input" maxLength={40} value={k.name} placeholder="Например, Массаж" onChange={(e) => set({ name: e.target.value })} />
      </div>
      <div className="field">
        <span className="label">Значок</span>
        <div className="chips" role="group" aria-label="Значок">
          {EMOJIS.map((e) => (
            <button key={e} type="button" className="chip emoji-chip" aria-pressed={k.emoji === e} aria-label={`Значок ${e}`} onClick={() => set({ emoji: e })}>
              {e}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span className="label">Категория — цвет на графиках</span>
        <div className="chips" role="group" aria-label="Категория">
          {CATS.map((c) => (
            <button key={c} type="button" className="chip" aria-pressed={k.cat === c} onClick={() => set({ cat: c })}>
              {CATEGORY_LABEL[c]}
            </button>
          ))}
        </div>
      </div>
      <Seg<'timed' | 'point'>
        label="Как отмечать"
        value={k.timed ? 'timed' : 'point'}
        options={[
          { value: 'timed', label: 'С длительностью' },
          { value: 'point', label: 'Момент' },
        ]}
        onChange={(v) => set({ timed: v === 'timed' })}
      />
      <span className="tiny muted">{k.timed ? 'Можно запустить таймер или указать начало и конец — время попадёт в шкалу суток.' : 'Отмечается одной точкой времени, например «дали капли».'}</span>
      <div className="field">
        <span className="label">Варианты (необязательно)</span>
        <div className="chips">
          {options.map((o) => (
            <span key={o} className="chip dir-chip">
              {o}
              <button type="button" aria-label={`Убрать «${o}»`} onClick={() => set({ options: options.filter((x) => x !== o) })}>
                ×
              </button>
            </span>
          ))}
        </div>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const v = opt.trim();
            if (!v || options.includes(v)) return;
            set({ options: [...options, v] });
            setOpt('');
          }}
        >
          <input className="input grow" maxLength={40} value={opt} placeholder="Например, лёгкий / сильный" onChange={(e) => setOpt(e.target.value)} aria-label="Новый вариант события" />
          <button type="submit" className="btn btn-soft" aria-label="Добавить вариант">
            <Icon name="plus" size={18} />
          </button>
        </form>
      </div>
      <div className="field">
        <label className="label" htmlFor="kind-unit">
          Единица для числа (необязательно)
        </label>
        <input id="kind-unit" className="input" maxLength={12} value={k.unit ?? ''} placeholder="мл, капли, раз…" onChange={(e) => set({ unit: e.target.value })} />
      </div>
      <div className="row">
        <button
          type="button"
          className="btn btn-primary grow"
          disabled={!valid}
          onClick={() => {
            const out: CustomKind = { id: k.id, name: k.name.trim(), emoji: k.emoji, cat: k.cat, timed: k.timed };
            if (options.length) out.options = options;
            if (k.unit?.trim()) out.unit = k.unit.trim();
            onSave(out);
          }}
        >
          Сохранить
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Отмена
        </button>
      </div>
      {!isNew && (
        <button type="button" className="link-btn danger" onClick={onDelete}>
          Удалить вид события
        </button>
      )}
      <span className="tiny muted">Уже сделанные записи останутся в дневнике, даже если вид удалить.</span>
    </div>
  );
}

function RemindersSection() {
  const ctx = useApp();
  const [s, setS] = useState<ReminderSettingsDto | null>(null);

  useEffect(() => {
    api.reminders(ctx.family.id).then(setS, ctx.fail);
  }, [ctx.family.id, ctx.fail]);

  async function save(next: ReminderSettingsDto) {
    setS(next);
    try {
      setS(await api.saveReminders(ctx.family.id, next));
    } catch (e) {
      ctx.fail(e);
    }
  }

  if (!s) return <div className="skeleton" />;
  const quiet = s.quietFrom !== null && s.quietTo !== null;
  return (
    <section className="card stack" aria-label="Напоминания в боте">
      <span className="section-title">Напоминания в боте</span>
      <div className="field">
        <span className="label">Напомнить о кормлении через</span>
        <Chips label="Интервал кормления" value={s.feedIntervalMin ? String(s.feedIntervalMin) : 'off'} options={INTERVALS} onChange={(v) => void save({ ...s, feedIntervalMin: v === 'off' ? null : Number(v) })} />
      </div>
      <div className="row">
        <span className="grow">Итог дня вечером</span>
        <Seg label="Итог дня" value={s.eveningSummary ? 'on' : 'off'} options={[{ value: 'off', label: 'Нет' }, { value: 'on', label: 'Да' }]} onChange={(v) => void save({ ...s, eveningSummary: v === 'on' })} />
      </div>
      {s.eveningSummary && (
        <div className="field">
          <label htmlFor="r-evening">Во сколько</label>
          <input id="r-evening" className="input" type="time" value={s.eveningAt} onChange={(e) => e.target.value && void save({ ...s, eveningAt: e.target.value })} />
        </div>
      )}
      <div className="row">
        <span className="grow">Тихие часы</span>
        <Seg label="Тихие часы" value={quiet ? 'on' : 'off'} options={[{ value: 'off', label: 'Нет' }, { value: 'on', label: 'Да' }]} onChange={(v) => void save({ ...s, quietFrom: v === 'on' ? '23:00' : null, quietTo: v === 'on' ? '07:00' : null })} />
      </div>
      {quiet && (
        <div className="tiles2">
          <div className="field">
            <label htmlFor="r-qf">С</label>
            <input id="r-qf" className="input" type="time" value={s.quietFrom ?? ''} onChange={(e) => e.target.value && void save({ ...s, quietFrom: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="r-qt">До</label>
            <input id="r-qt" className="input" type="time" value={s.quietTo ?? ''} onChange={(e) => e.target.value && void save({ ...s, quietTo: e.target.value })} />
          </div>
        </div>
      )}
      <p className="tiny muted">Чтобы бот мог писать, откройте его чат и нажмите «Старт».</p>
    </section>
  );
}

export function Settings({ onClose }: { onClose: () => void }) {
  const ctx = useApp();
  return (
    <div className="stack">
      <header className="header">
        <button type="button" className="icon-btn" aria-label="Назад" onClick={onClose}>
          <Icon name="back" size={18} />
        </button>
        <h1 className="h2 grow">Настройки</h1>
      </header>
      <ChildrenSection />
      <FamilySection />
      <RemindersSection />
      <CustomKindsSection />
      <DirectoriesSection />
      <section className="card stack" aria-label="Оформление">
        <span className="section-title">Оформление</span>
        <Seg<ThemeMode>
          label="Тема"
          value={ctx.themeMode}
          options={[
            { value: 'auto', label: 'Авто' },
            { value: 'day', label: 'День' },
            { value: 'night', label: 'Ночь' },
          ]}
          onChange={ctx.setThemeMode}
        />
        <p className="tiny muted">«Авто» включает ночную тему с 22:00 до 7:00 и когда в Telegram тёмная тема.</p>
      </section>
    </div>
  );
}
